// I colori dell'app non sono scritti qui: qui ci sono i loro nomi. Il valore
// vero sta in `src/theme.css`, una volta per il tema chiaro e una per quello
// scuro, e il browser lo sostituisce da solo quando il tema cambia.
//
// Vuol dire che `theme.colors.gunmetal` continua a funzionare come sempre —
// in un `bg`, in un `textColor`, dentro atomize — ma non è più un colore
// fisso: è il posto dove il colore andrà a finire. Cambiare tema non
// ricalcola niente di React, riscrive quattro righe di variabili CSS.
//
// Due nomi meritano una spiegazione, perché nel chiaro sembrano doppioni:
//
// - `gunmetal` è l'inchiostro (scritte e icone) e nello scuro diventa
//   chiaro; `gunmetal_fill` è il petrolio pieno sotto a una scritta bianca
//   (pastiglie, bottoni, pallini) e nello scuro resta scuro, altrimenti la
//   scritta bianca sopra sparirebbe
// - `white` è il bianco vero, quello che sta sopra a un pieno colorato;
//   `surface` è il fondo delle schede, che nello scuro è petrolio
const theme = {
	colors: {
		primary: "var(--c-primary)",
		primary_light: "var(--c-primary-light)",
		background: "var(--c-background)",
		background_blur: "var(--c-background-blur)",
		surface: "var(--c-surface)",
		accent: "var(--c-accent)",
		accent_light: "var(--c-accent-light)",
		gunmetal: "var(--c-gunmetal)",
		gunmetal_fill: "var(--c-gunmetal-fill)",
		gunmetal_light: "var(--c-gunmetal-light)",
		gunmetal_lighter: "var(--c-gunmetal-lighter)",
		gray_light: "var(--c-gray-light)",
		text: "var(--c-ink)",
		text_light: "var(--c-text-light)",
		primary_blur: "var(--c-primary-blur)",
		accent_blur: "var(--c-accent-blur)",
		gunmetal_blur: "var(--c-gunmetal-blur)",
		gray_blur: "var(--c-gray-blur)",
		white: "var(--c-white)",
		// Il nero di atomize è il colore del testo di tutta la pagina
		// (`StyleReset` lo scrive su `body`): puntandolo all'inchiostro, ogni
		// scritta che non chiede un colore suo segue il tema da sola.
		black: "var(--c-ink)",
		overlay: "var(--c-overlay)",
		// Due nomi che arrivano da atomize e che qualche componente suo usa
		// da solo (le frecce dei menu a tendina, il testo dei campi): nel
		// chiaro valgono quello che valevano, nello scuro seguono il tema.
		medium: "var(--c-medium)",
		dark: "var(--c-ink)",
		error: "var(--c-error)",
		red: "var(--c-red)",
		red_light: "var(--c-red-light)",
		warning: "var(--c-warning)",
		yellow: "var(--c-yellow)",
		yellow_light: "var(--c-yellow-light)",
		blue: "var(--c-blue)",
		blue_light: "var(--c-blue-light)",
		green: "var(--c-green)",
		green_light: "var(--c-green-light)",
		level_red: "var(--c-level-red)",
		level_orange: "var(--c-level-orange)",
		level_green: "var(--c-level-green)",
		level_yellow: "var(--c-level-yellow)",
		clay: "var(--c-clay)",
		grass: "var(--c-grass)",
		hard: "var(--c-hard)",
		pri: "var(--c-pri)",
		pri_dark: "var(--c-pri-dark)",
		pri_fill: "var(--c-pri-fill)",
		blue_t: "var(--c-blue-t)",
		blue_t_dark: "var(--c-blue-t-dark)",
		blue_t_fill: "var(--c-blue-t-fill)",
		sat: "var(--c-sat)",
		sat_dark: "var(--c-sat-dark)",
		sat_fill: "var(--c-sat-fill)",
		cad: "var(--c-cad)",
		cad_dark: "var(--c-cad-dark)",
		cad_fill: "var(--c-cad-fill)",
		ric: "var(--c-ric)",
		ric_dark: "var(--c-ric-dark)",
		ric_fill: "var(--c-ric-fill)",
		ce: "var(--c-ce)",
		ce_dark: "var(--c-ce-dark)",
		ce_fill: "var(--c-ce-fill)",
		lb: "var(--c-lb)",
		lb_dark: "var(--c-lb-dark)",
		lb_fill: "var(--c-lb-fill)",
		match: "var(--c-match)",
		match_dark: "var(--c-match-dark)",
		match_fill: "var(--c-match-fill)",
		// Lo schema di un esercizio: un disegno, uguale nei due temi
		schema_grass: "var(--c-schema-grass)",
		schema_court: "var(--c-schema-court)",
		schema_inside: "var(--c-schema-inside)",
		schema_line: "var(--c-schema-line)",
		schema_net: "var(--c-schema-net)",
		schema_mesh: "var(--c-schema-mesh)",
		schema_shadow: "var(--c-schema-shadow)",
		schema_shade: "var(--c-schema-shade)",
		schema_shine: "var(--c-schema-shine)",
		schema_ball: "var(--c-schema-ball)",
		schema_badge: "var(--c-schema-badge)",
		schema_coach: "var(--c-schema-coach)",
		schema_cone: "var(--c-schema-cone)",
		schema_cone_dark: "var(--c-schema-cone-dark)",
		schema_zone: "var(--c-schema-zone)",
		schema_zone_line: "var(--c-schema-zone-line)",
		schema_veil: "var(--c-schema-veil)",
		schema_dim: "var(--c-schema-dim)",
		schema_red: "var(--c-schema-red)",
		schema_orange: "var(--c-schema-orange)",
		schema_teal: "var(--c-schema-teal)",
		schema_violet: "var(--c-schema-violet)",
		schema_yellow: "var(--c-schema-yellow)",
		schema_brown: "var(--c-schema-brown)",
	},
	fontFamily: {
		primary: "Poppins, sans-serif",
	},
	shadows: {
		"up-3": "var(--shadow-up-3)",
		"top-3": "var(--shadow-top-3)",
		accent: "var(--shadow-accent)",
		lift: "var(--shadow-lift)",
	},
	rounded: {
		upOnly: "12px 12px 0 0",
		bottomOnly: "0 0 12px 12px",
		"20px": "20px",
		"25px": "25px",
		"15px": "15px",
		"5px": "5px",
		"l-20px": "20px 0 0 20px",
		"r-20px": "0 20px 20px 0",
		"t-20px": "20px 20px 5px 5px",
		"b-20px": "5px 5px 20px 20px",
		"pill-15px": "0 15px 0 15px",
		"pillAlt-20px": "20px 0 40px 20px",
		"pillAltRev-20px": "40px 20px 20px 0",
	},
	transition: {
		fast: "all 0.2s ease-in-out",
	},
}

// Da questa data in poi le lezioni salvano `partecipantIDs`, l'array su cui
// Firestore cerca le lezioni di un allievo: lo storico parte da qui.
// Cambiando questa data va rilanciato scripts/backfillLessonPartecipantIDs.js
const lessonsHistoryStart = "2026-08-01"

// Il progetto Firebase vero. Tutto quello che non è questo è un banco di
// prova, e l'app lo dice a chi la sta guardando.
const productionProjectID = "hdtennisteammanager"

const config = {
	theme: theme,
	lessonsHistoryStart: lessonsHistoryStart,
	productionProjectID: productionProjectID,
}

module.exports = config
